/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import {
    IAZDatastore,
    IDatastoreInfo,
    IVcenterSharedDatastore,
} from 'generated-types';

import { AZDatastore } from 'object-types';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

// API Urls.
const NSX_DATASTORE_API = 'api/vcenter/datastores';
const DATASTORE_API = 'api/vimgrvcenterruntime/datastores/';

/**
 * @description
 *
 *   Az Datastore Message Item.
 *
 * @author Sarthak kapoor
 */
export class AZDatastoreConfigItem extends withFullModalMixin(MessageItem)<IAZDatastore> {
    protected static ngDependencies = [
        HTTP_WRAPPER_TOKEN,
    ];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: AZDatastore,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();
    }

    /**
     * Fetch NSX Cloud Data Store list.
     */
    public async fetchNsxCloudDataStoreList(
        cloudId: string,
        vcenterId: string,
        isInvokedFromParentModal = false,
    ): Promise<IVcenterSharedDatastore[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: NSX_DATASTORE_API,
                method: HttpMethod.POST,
                data: {
                    cloud_uuid: cloudId,
                    vcenter_uuid: vcenterId,
                },
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { vcenter_datastores: vcenterDatastores = [] } = resource;

            return vcenterDatastores;
        } catch (error) {
            if (isInvokedFromParentModal) {
                throw error;
            }

            this.errors = error.data;
        } finally {
            this.busy = false;
        }
    }

    /**
     * Fetch vcenter cloud Datastore list.
     */
    public async fetchVcenterDataStoreList(cloudId: string): Promise<IDatastoreInfo[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: `${DATASTORE_API}${cloudId}`,
                method: HttpMethod.GET,
            };

            const response = await this.httpWrapper.request(requestConfig);

            return response.data[0].datastores;
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.busy = false;
        }
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AzDatastoreModalComponent,
        } = await import(
            /* webpackChunkName: "availability-zone-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/availability-zone-modal/az-datastore-modal/az-datastore-modal.component'
        );

        return AzDatastoreModalComponent as Type<Component>;
    }
}
